import {
  CURRENT_ROW_OBJECT,
  EMPTY_EXPANSION_COLUMN,
  ExpansionColumnDefinition,
  ResponsiveTableColumnsDefinition
} from '@aviary/components/table/table.component.types';
import {ColumnDefinition} from '@store/aviary/aviary.adapter';
import {BalanceSelectors} from '@store/balance/balance.selectors';
import {PatronBalanceTransaction} from '@store/balance/balance.types';
import {CombinedBalanceTableRowComponent} from '@app/my-account/balance/table-column-components/combined-balance-table-row.component';
import {BalanceTableAmountDueMobileColumnComponent} from '@app/my-account/balance/table-column-components/balance-table-amount-due-mobile-column.component';
import {ExpandedTransactionNamesComponent} from '@app/my-account/balance/expansion-rows/expanded-transaction-names.component';
import {ExpandedTransactionAmountsComponent} from '@app/my-account/balance/expansion-rows/expanded-transaction-amounts.component';
import {createSelector} from '@ngrx/store';

type BalanceSelectorBucket = typeof BalanceSelectors.BalanceTransactions | typeof BalanceSelectors.DependentBalanceTransactions;

export function getTransactionsTableColumnsDefinition(selectorBucket: BalanceSelectorBucket): ColumnDefinition<PatronBalanceTransaction, BalanceSelectorBucket>[] {
  const descriptionSelector =
    (balanceTransaction: PatronBalanceTransaction) => createSelector(
      selectorBucket.showAmountsExpansionFor(balanceTransaction),
      selectorBucket.nameFor(balanceTransaction),
      (showExpansion, name) => showExpansion ? 'Multiple' : name
    );
  const amountDueSelector =
    (balanceTransaction: PatronBalanceTransaction) => createSelector(
      selectorBucket.amountOutstandingFor(balanceTransaction),
      selectorBucket.typeFor(balanceTransaction),
      (amountOwed, type) => amountOwed <= 0 && type !== 'PAY_FROM_EXTERNAL' ? amountOwed : ''
    );

  return [
    {
      type: 'text',
      value: 'createdFor',
      header: 'Date',
      headerCssClasses: 'w-[7.25rem]',
      format: 'DATE_ONLY',
    },
    {
      type: 'text',
      value: 'idFor',
      header: 'Transaction ID',
      headerCssClasses: 'w-[8rem]',
    },
    {
      type: 'component',
      columnName: 'itemTitle',
      header: 'Item',
      componentClass: CombinedBalanceTableRowComponent,
      componentProperties: {
        title: 'itemTitleFor'
      }
    },
    {
      overrideColumnName: 'description',
      type: 'text',
      header: 'Description',
      value: descriptionSelector,
    },
    {
      type: 'component',
      columnName: 'itemMd',
      header: 'Description',
      componentClass: CombinedBalanceTableRowComponent,
      componentProperties: {
        description: descriptionSelector,
        title: 'itemTitleFor'
      }
    },
    {
      type: 'component',
      columnName: 'itemSm',
      componentClass: CombinedBalanceTableRowComponent,
      componentProperties: {
        description: descriptionSelector,
        title: 'itemTitleFor',
        transactionId: 'idFor',
        date: 'createdFor',
        amount: 'amountFor'
      }
    },
    {
      type: 'text',
      value: 'amountFor',
      header: 'Amount',
      format: 'POSITIVE_CURRENCY',
      headerCssClasses: 'w-[7.25rem]',
      cssClasses: {
        'text-primary': 'hasBalanceReducingTransactionTypeFor'
      }
    },
    {
      overrideColumnName: 'amountDue',
      type: 'text',
      value: amountDueSelector,
      header: 'Amount Due',
      format: 'POSITIVE_CURRENCY',
      headerCssClasses: 'w-[7.25rem]',
      cssClasses: {
        'text-error': 'amountOutstandingIsNegativeFor'
      }
    },
    {
      type: 'component',
      columnName: 'amountDueSm',
      componentClass: BalanceTableAmountDueMobileColumnComponent,
      componentProperties: {
        amountDue: amountDueSelector,
        isNegative: 'amountOutstandingIsNegativeFor'
      }
    }
  ];
}

export const TRANSACTIONS_TABLE_RESPONSIVE_DEFINITION: ResponsiveTableColumnsDefinition = {
  columnNamesSm: ['itemSm', 'amountDueSm'],
  columnNamesMd: ['created', 'id', 'itemMd', 'amount', 'amountDue'],
  columnNamesLg: ['created', 'id', 'itemTitle', 'description', 'amount', 'amountDue'],
  omitActionsSm: true,
  responsiveHideHeaderRow: true,
};

export const TRANSACTIONS_EXPANSION_DESCRIPTION_COLUMN_DEFINITION: ExpansionColumnDefinition<PatronBalanceTransaction, BalanceSelectorBucket> = {
  type: 'component',
  header: 'Description:',
  componentClass: ExpandedTransactionNamesComponent,
  componentProperties: {
    transaction: CURRENT_ROW_OBJECT
  }
};

export const TRANSACTIONS_EXPANSION_AMOUNTS_COLUMN_DEFINITION: ExpansionColumnDefinition<PatronBalanceTransaction, BalanceSelectorBucket> = {
  type: 'component',
  header: 'Amount:',
  headerCssClasses: 'text-right md:text-left',
  componentClass: ExpandedTransactionAmountsComponent,
  componentProperties: {
    transaction: CURRENT_ROW_OBJECT
  }
};

export const TRANSACTIONS_TABLE_EXPANSION_COLUMNS: ExpansionColumnDefinition<PatronBalanceTransaction, BalanceSelectorBucket>[] = [
  EMPTY_EXPANSION_COLUMN, // for created column
  EMPTY_EXPANSION_COLUMN, // for id column
  EMPTY_EXPANSION_COLUMN, // for itemTitle column
  TRANSACTIONS_EXPANSION_DESCRIPTION_COLUMN_DEFINITION, // for description column (lg view)
  TRANSACTIONS_EXPANSION_DESCRIPTION_COLUMN_DEFINITION, // for itemMd column
  TRANSACTIONS_EXPANSION_DESCRIPTION_COLUMN_DEFINITION, // for itemSm column
  TRANSACTIONS_EXPANSION_AMOUNTS_COLUMN_DEFINITION, // for amount column (lg view)
  EMPTY_EXPANSION_COLUMN, // for amountDue column
  TRANSACTIONS_EXPANSION_AMOUNTS_COLUMN_DEFINITION, // for amountDueSm column
];
